import {
  Box,
  Button,
  Flex,
  Form,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useVerificationFlow } from '../../../../../../../shared/VerificationFlow'
import { emailRegex } from '../../../../../../../../constants/regexes'
import { useAddPartnerStep } from '../../../../../../../../hooks/homebotNetwork/verificationFlow/useAddPartnerStep'

const MSG = defineMessages({
  addPartner: {
    id: 'VerificationFlowModal.addPartner',
    defaultMessage: 'Do you have a different {customerType} you would like to add?'
  },
  addPartnerSubtext: {
    id: 'ScheduleTour.VerificationFlow.AddPartnerView.addPartnerSubtext',
    defaultMessage: 'If you do not have an agent to add, the tour will be scheduled with the listing agent.'
  },
  add: {
    id: 'AddPreferredAgentForm.add',
    defaultMessage: 'Yes, add'
  },
  email: {
    id: 'AddPreferredAgentForm.email',
    defaultMessage: 'Email'
  },
  emailInvalid: {
    id: 'AddPreferredAgentForm.emailInvalid',
    defaultMessage: 'Invalid email address'
  },
  emailRequired: {
    id: 'AddPreferredAgentForm.emailRequired',
    defaultMessage: 'Email is required'
  },
  error: {
    id: 'AddPreferredAgentForm.error',
    defaultMessage: 'Failed to add partner'
  },
  firstName: {
    id: 'AddPreferredAgentForm.firstName',
    defaultMessage: 'First name'
  },
  firstNameRequired: {
    id: 'AddPreferredAgentForm.firstNameRequired',
    defaultMessage: 'First name is required'
  },
  lastName: {
    id: 'AddPreferredAgentForm.lastName',
    defaultMessage: 'Last name'
  },
  lastNameRequired: {
    id: 'AddPreferredAgentForm.lastNameRequired',
    defaultMessage: 'Last name is required'
  },
  notAtThisTime: {
    id: 'AddPreferredAgentForm.notAtThisTime',
    defaultMessage: 'Not at this time'
  },
  // Shared a lot
  terms: {
    id: 'VerificationFlow.UnverifiedOptions.terms',
    defaultMessage:
      "By clicking 'Yes, Add' you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your contact information (name and email address) and any other information you include in your question with the agent identified above. You agree that the agent may contact you about your inquiry."
  }
})

/**
 * AddPartnerView specific to the Schedule Tour flow
 *
 * @returns AddPartnerView
 */
export const AddPartnerView = () => {
  const intl = useIntl()
  const toast = useToast()
  const { isLoading, onCancel, addPartner } = useAddPartnerStep()
  const { state } = useVerificationFlow()
  const { removedPartnerType, currentPartnerType } = state
  const partnerTypeForTitle = removedPartnerType || currentPartnerType

  const title = intl.formatMessage(MSG.addPartner, {
    customerType: partnerTypeForTitle === 'LoanOfficer' ? 'Loan Officer' : 'Real Estate Agent'
  })

  const handleAddPartner = async data => {
    try {
      await addPartner(data)
    } catch (error) {
      toast({
        description: intl.formatMessage(MSG.error),
        status: 'error'
      })
    }
  }

  return (
    <Flex direction='column' justifyContent='space-between' data-qa='schedule-tour-unverified-view' h='100%'>
      {/* @ts-expect-error - there is an open request to allow this prop on the Form component */}
      <Form onSubmit={handleAddPartner} noValidate>
        {({ formState: { errors } }) => (
          <>
            <Box>
              <Heading textAlign='left'>{title}</Heading>
              <Text color='neutral.700'>{intl.formatMessage(MSG.addPartnerSubtext)}</Text>
            </Box>
            <Stack gap={4}>
              <Flex direction='column' gap={4}>
                <FormControl isRequired isInvalid={!!errors.firstName}>
                  <FormLabel>{intl.formatMessage(MSG.firstName)}</FormLabel>
                  <Input
                    name='firstName'
                    placeholder={intl.formatMessage(MSG.firstName)}
                    required
                    tracking={{
                      guid: '52Xv8anr1BxfCniup25nkF',
                      ui_context: 'schedule_tour_verification_flow_add_partner_view',
                      descriptor: MSG.firstName.defaultMessage
                    }}
                    rules={{ required: `${intl.formatMessage(MSG.firstNameRequired)}` }}
                  />
                  {errors.firstName ? (
                    <>
                      <FormErrorMessage>{errors.firstName.message?.toString()}</FormErrorMessage>
                    </>
                  ) : (
                    <>
                      <FormHelperText>{'\u200B'}</FormHelperText>
                    </>
                  )}
                </FormControl>
                <FormControl isRequired isInvalid={!!errors.lastName}>
                  <FormLabel>{intl.formatMessage(MSG.lastName)}</FormLabel>
                  <Input
                    name='lastName'
                    placeholder={intl.formatMessage(MSG.lastName)}
                    required
                    tracking={{
                      guid: 'oJBW9rsQtfaRfY1MMufSwt',
                      ui_context: 'schedule_tour_verification_flow_add_partner_view',
                      descriptor: MSG.lastName.defaultMessage
                    }}
                    rules={{ required: `${intl.formatMessage(MSG.lastNameRequired)}` }}
                  />
                  {errors.lastName ? (
                    <>
                      <FormErrorMessage>{errors.lastName.message?.toString()}</FormErrorMessage>
                    </>
                  ) : (
                    <>
                      <FormHelperText>{'\u200B'}</FormHelperText>
                    </>
                  )}
                </FormControl>
                <FormControl isRequired isInvalid={!!errors.email}>
                  <FormLabel>{intl.formatMessage(MSG.email)}</FormLabel>
                  <Input
                    name='email'
                    type='email'
                    placeholder={intl.formatMessage(MSG.email)}
                    required
                    tracking={{
                      guid: '6XNxvn8rhQZNq7dJkhwgiM',
                      ui_context: 'schedule_tour_verification_flow_add_partner_view',
                      descriptor: MSG.email.defaultMessage
                    }}
                    rules={{
                      // TODO: Share these w/ tune value form translations
                      required: intl.formatMessage(MSG.emailRequired),
                      pattern: {
                        value: emailRegex,
                        message: intl.formatMessage(MSG.emailInvalid)
                      }
                    }}
                  />
                  {errors.email ? (
                    <>
                      <FormErrorMessage>{errors.email.message?.toString()}</FormErrorMessage>
                    </>
                  ) : (
                    <>
                      <FormHelperText>{'\u200B'}</FormHelperText>
                    </>
                  )}
                </FormControl>
              </Flex>

              <Stack direction='row' gap={2} justifyContent='flex-end'>
                <Button
                  variant='solid'
                  colorScheme='secondary'
                  isDisabled={isLoading}
                  onClick={onCancel}
                  tracking={{
                    guid: '7hCnG7XiJQh49Y8v7JcfEh',
                    ui_context: 'schedule_tour_verification_flow_add_partner_view',
                    descriptor: MSG.notAtThisTime.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.notAtThisTime)}
                </Button>
                <Button
                  mr={3}
                  colorScheme='primary'
                  type='submit'
                  isLoading={isLoading}
                  tracking={{
                    guid: 'gAiRwwRh8hhvKnJmNhyPig',
                    ui_context: 'schedule_tour_verification_flow_add_partner_view',
                    descriptor: MSG.add.defaultMessage
                  }}
                >
                  {intl.formatMessage(MSG.add)}
                </Button>
              </Stack>
            </Stack>
            <Box bg='neutral.200' borderTop='1px solid' borderColor='neutral.300' mx='-6' mt='6' mb='-6' p='6'>
              <Text size='3xs' color='neutral.600'>
                {intl.formatMessage(MSG.terms)}
              </Text>
            </Box>
          </>
        )}
      </Form>
    </Flex>
  )
}
